// Thin, locally-typed wrappers over the AHP host's `extensions/*Project` vendor
// commands (not in the protocol's typed `CommandMap`). Only the launch-path
// subset (listProjects, cloneProject) is exposed.
// Spec: github/copilot-host docs/architecture/workspace.md

import type {AhpClient} from '@microsoft/agent-host-protocol/client'
import type {ProjectEntry} from './project-catalogue'

const CMD_LIST_PROJECTS = 'extensions/listProjects'
const CMD_CLONE_PROJECT = 'extensions/cloneProject'

/** Send a vendor `extensions/*` request the typed `CommandMap` doesn't cover, via one contained cast. */
function vendorRequest<TResult>(client: AhpClient, method: string, params: unknown): Promise<TResult> {
  const send = client.request as unknown as (method: string, params: unknown) => Promise<TResult>
  return send.call(client, method, params)
}

interface ListProjectsResult {
  projects?: ProjectEntry[]
}

/** Enumerate the host's current projects catalogue. */
export async function listProjects(client: AhpClient): Promise<ProjectEntry[]> {
  const result = await vendorRequest<ListProjectsResult>(client, CMD_LIST_PROJECTS, {})
  return Array.isArray(result?.projects) ? result.projects : []
}

export interface CloneProjectParams {
  /** Clonable URL (https/ssh). The clone lands at `<targetRoot>/<owner>/<repo>`. */
  url: string
  /** Clone depth. Current host flow requires shallow clone depth 1. */
  depth: 1
  targetRoot?: string
}

interface CloneProjectResult {
  project: ProjectEntry
}

/**
 * Start cloning a repository as a project. Resolves with the provisional
 * `cloning` entry; the authoritative `path`/`defaultBranch` arrive later via
 * `root/configChanged`, so watch the catalogue rather than trust this response.
 */
export async function cloneProject(client: AhpClient, params: CloneProjectParams): Promise<ProjectEntry> {
  const result = await vendorRequest<CloneProjectResult>(client, CMD_CLONE_PROJECT, {
    url: params.url,
    depth: params.depth,
    ...(params.targetRoot ? {targetRoot: params.targetRoot} : {}),
  })
  return result.project
}
